import ModulePageLayout from "./ModulePageLayout";
import { Link } from "react-router-dom";
import {
  ScanLine,
  PackageSearch,
  Boxes,
  ClipboardList,
  Workflow,
  ShieldCheck,
} from "lucide-react";

const CAPABILITIES = [
  {
    icon: PackageSearch,
    title: "Purchase administration",
    body: "Supplier orders, approvals and inventory stay in sync with the rest of the ledger. No separate purchasing silo.",
  },
  {
    icon: Boxes,
    title: "Multi-level inventory & BOM",
    body: "Raw materials, semi-finished and finished goods are tracked as one versioned structure with cost roll-up at every level.",
  },
  {
    icon: ScanLine,
    title: "Full lot traceability",
    body: "Lot numbers, expiry tracking and one-click recall across every step of the supply chain, from supplier to shipped order.",
  },
  {
    icon: ClipboardList,
    title: "Work orders & production planning",
    body: "Production orders, capacity planning and the schedule calendar are synced to inventory and finance in real time.",
  },
  {
    icon: Workflow,
    title: "Operations & process layer",
    body: "Map, automate and monitor the operational flows around receiving, QC, production and dispatch alongside the stock itself.",
  },
  {
    icon: ShieldCheck,
    title: "EU-hosted, GDPR-ready",
    body: "Data is hosted in the EU with audit trails and role-based access built in from day one.",
  },
];

const USE_CASES = [
  "Food & beverage producers managing recipes, allergens and lot recalls.",
  "Cosmetics brands coordinating multi-component formulas and batch QC.",
  "Pharma & supplement SMEs with regulated traceability requirements.",
  "Light manufacturing teams running BOMs, work orders and purchasing from one place.",
];

export default function SupplyChainModule() {
  return (
    <ModulePageLayout
      title="Supply chain module — Zynthoro AI ERP for SMEs"
      description="Zynthoro's supply chain module connects purchasing, inventory, BOMs, production and finance in one AI-native ERP. Full lot traceability for European SMEs."
      breadcrumbLabel="Supply chain"
      heroEyebrow="Modules · Supply chain"
      heroHeading="One supply chain. One ledger. One AI."
      heroBody="Zynthoro's supply chain capability is part of a single AI-native ERP. Purchasing, inventory, BOMs, production orders and finance share one data model, so stock movements, costs and margins stay consistent across the business."
    >
      {/* What it covers */}
      <section className="zy-section" style={{ background: "#fff" }}>
        <div className="zy-container">
          <div className="max-w-3xl mb-10 zy-reveal">
            <h2 className="zy-h2">What the supply chain module covers</h2>
            <p className="zy-body mt-4">
              Supply chain is not a separate product — it is a coordinated set of capabilities
              inside the broader Zynthoro ERP ecosystem. Each capability below is already available
              as part of the connected platform.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPABILITIES.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  className="zy-reveal p-6 rounded-2xl border bg-white"
                  style={{ borderColor: "#eee" }}
                  data-testid={`supply-chain-cap-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <span
                    className="inline-flex items-center justify-center w-11 h-11 rounded-xl mb-4"
                    style={{ background: "#EAF0FF" }}
                  >
                    <Icon size={20} style={{ color: "#1A4FFF" }} />
                  </span>
                  <h3 className="text-[16px] font-bold tracking-tight mb-1.5">{c.title}</h3>
                  <p className="text-[13.5px] text-[#555] leading-relaxed">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who it is for */}
      <section className="zy-section" style={{ background: "#F6F8FB" }}>
        <div className="zy-container">
          <div className="max-w-3xl zy-reveal">
            <h2 className="zy-h2">Who Zynthoro's supply chain capability is designed for</h2>
            <p className="zy-body mt-4">
              Zynthoro is built for European small and medium businesses that have outgrown
              spreadsheets and single-purpose stock tools, but do not want the cost and
              implementation weight of a traditional enterprise ERP.
            </p>
            <ul className="mt-6 space-y-2.5 zy-body">
              {USE_CASES.map((u) => (
                <li key={u} className="flex gap-2.5">
                  <span style={{ color: "#1A4FFF" }}>·</span>
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* How it fits */}
      <section className="zy-section" style={{ background: "#fff" }}>
        <div className="zy-container">
          <div className="max-w-3xl zy-reveal">
            <h2 className="zy-h2">How supply chain fits in the Zynthoro ERP ecosystem</h2>
            <p className="zy-body mt-4">
              Receiving a goods receipt updates stock, costs and the general ledger in the same
              transaction. A production order consumes raw materials, generates finished goods
              and lands the production cost on the right finance account. Lot numbers flow from
              purchase order through QC to customer invoice, so a recall is one query, not an
              archaeology project.
            </p>
            <p className="zy-body mt-4">
              The AI assistant layer sits on top of this shared data model and can answer
              questions about stock, margins and supplier performance without exporting
              anything to a separate BI tool.
            </p>
          </div>
        </div>
      </section>

      {/* Answer-first FAQ */}
      <section className="zy-section" style={{ background: "#F6F8FB" }}>
        <div className="zy-container">
          <div className="max-w-3xl zy-reveal">
            <h2 className="zy-h2">Supply chain — common questions</h2>

            <div className="mt-6">
              <h3 className="text-[17px] font-bold tracking-tight">Is Zynthoro a dedicated supply chain platform?</h3>
              <p className="zy-body mt-2">
                No. Zynthoro is an AI-native business operating system for SMEs. Supply chain is
                one of twelve connected modules, sharing a single data model with finance,
                sales, HR, projects, operations and marketing.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="text-[17px] font-bold tracking-tight">Does Zynthoro support full lot traceability?</h3>
              <p className="zy-body mt-2">
                Yes. Lot numbers and expiry data are tracked from goods receipt through production
                and dispatch. A recall query returns every batch that touched a given lot across
                the entire supply chain.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="text-[17px] font-bold tracking-tight">Can I run multi-level BOMs and production orders?</h3>
              <p className="zy-body mt-2">
                Yes. BOMs are versioned and support raw → semi-finished → finished structures with
                cost roll-up at every level. Production orders consume stock, generate finished
                goods and update finance in the same transaction.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="text-[17px] font-bold tracking-tight">Where is my data hosted?</h3>
              <p className="zy-body mt-2">
                Customer data is hosted in the European Union. Zynthoro ships with GDPR-ready
                controls, audit trails and role-based access.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related content */}
      <section className="zy-section" style={{ background: "#fff" }}>
        <div className="zy-container">
          <div className="max-w-3xl zy-reveal">
            <h2 className="zy-h2">Related reading</h2>
            <ul className="mt-5 space-y-2.5 zy-body">
              <li><Link to="/blog/supply-chain-management-platform" className="zy-link">10 Best Supply Chain Management Platforms for SMEs in 2026</Link></li>
              <li><Link to="/blog/ai-in-supply-chain-management" className="zy-link">AI in Supply Chain Management for Small Businesses</Link></li>
              <li><Link to="/modules/manufacturing" className="zy-link">Manufacturing module</Link> · <Link to="/modules/compliance" className="zy-link">Compliance module</Link></li>
            </ul>
          </div>
        </div>
      </section>
    </ModulePageLayout>
  );
}
